import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { platformUserApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import {
  Badge,
  Button,
  ConfirmDialog,
  DropdownMenu,
  DataTable,
  DataView,
  PageHeader,
  PageLayout,
  PageToolbar,
  SearchField,
  StatusBadge,
} from '../components/common'
import UserAccessDialog from '../components/UserAccessDialog'
import InviteLinkDialog from '../components/InviteLinkDialog'
import { ROLES, formatRoleLabel } from '../constants/roles'
import styles from './WagerTeamPage.module.css'

const SCOPE_LABELS = {
  selected: 'Selected organizations',
  all_current: 'All current organizations',
  all: 'All current and future organizations',
}

function organizationRoleSummary(user) {
  const activeMemberships = user.organizations.filter((organization) => organization.is_active)
  const memberships = activeMemberships.length ? activeMemberships : user.organizations
  const roles = [...new Set(memberships.map((organization) => organization.role))]
  if (!roles.length) {
    return { label: formatRoleLabel(user.default_role), detail: 'No organization roles' }
  }
  if (roles.length === 1) {
    return {
      label: formatRoleLabel(roles[0]),
      detail: activeMemberships.length ? null : 'No active roles',
    }
  }
  return {
    label: 'Mixed roles',
    detail: roles.map(formatRoleLabel).join(', '),
  }
}

export function WagerTeamPage() {
  const { user: currentUser } = useAuth()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [editingUser, setEditingUser] = useState(null)
  const [inviting, setInviting] = useState(false)
  const [cancelInviteUser, setCancelInviteUser] = useState(null)
  const [inviteLink, setInviteLink] = useState(null)
  const [creatingInviteLink, setCreatingInviteLink] = useState(false)

  const usersQuery = useQuery({
    queryKey: ['platform-users'],
    queryFn: () => platformUserApi.list(),
  })
  const organizationsQuery = useQuery({
    queryKey: ['platform-user-organizations'],
    queryFn: () => platformUserApi.listOrganizations(),
  })

  const users = useMemo(() => usersQuery.data?.users || [], [usersQuery.data])
  const organizations = useMemo(() => organizationsQuery.data?.organizations || [], [organizationsQuery.data])
  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) return users
    return users.filter((user) => (
      user.email.toLowerCase().includes(query)
      || user.display_name?.toLowerCase().includes(query)
      || user.organizations.some((organization) => organization.name.toLowerCase().includes(query))
    ))
  }, [searchTerm, users])

  const updateMutation = useMutation({
    mutationFn: ({ userId, access }) => platformUserApi.updateAccess(userId, access),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-users'] })
      setEditingUser(null)
      toast.success('Organization access updated')
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Organization access could not be updated'),
  })
  const inviteMutation = useMutation({
    mutationFn: (request) => platformUserApi.invite(request),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['platform-users'] })
      setInviting(false)
      toast.success(response.message || 'Invitation sent')
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Invitation could not be sent'),
  })

  const resendInvitationMutation = useMutation({
    mutationFn: (userId) => platformUserApi.resendInvitation(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-users'] })
      toast.success('Invitation resent')
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to resend invitation'),
  })
  const cancelInvitationMutation = useMutation({
    mutationFn: (userId) => platformUserApi.cancelInvitation(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-users'] })
      setCancelInviteUser(null)
      toast.success('Invitation cancelled')
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to cancel invitation'),
  })
  const createInvitationLink = async (userId) => {
    setCreatingInviteLink(true)
    try {
      const { url, expires_at } = await platformUserApi.createInvitationLink(userId)
      setInviteLink({ url, expires_at })
      queryClient.invalidateQueries({ queryKey: ['platform-users'] })
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create invite link')
    } finally {
      setCreatingInviteLink(false)
    }
  }

  // Resend and Copy Invite Link each issue a new code, which kills the
  // other's link, so neither starts while the other is in flight.
  const reissuingInvite = resendInvitationMutation.isPending || creatingInviteLink

  const openInvite = () => {
    inviteMutation.reset()
    setInviting(true)
  }
  const closeInvite = () => {
    inviteMutation.reset()
    setInviting(false)
  }
  const openEditor = (user) => {
    updateMutation.reset()
    setEditingUser(user)
  }
  const closeEditor = () => {
    updateMutation.reset()
    setEditingUser(null)
  }

  const columns = [
    {
      key: 'email',
      label: 'User',
      sortable: true,
      render: (row) => (
        <span className={styles.identity}>
          <strong>{row.display_name || row.email}</strong>
          <span>{row.email}</span>
        </span>
      ),
    },
    {
      key: 'scope',
      label: 'Organization access',
      render: (row) => (
        <span className={styles.scope}>
          <strong>{SCOPE_LABELS[row.scope] || SCOPE_LABELS.selected}</strong>
          <span>{row.organizations.filter((organization) => organization.is_active).length} active</span>
        </span>
      ),
    },
    {
      key: 'roles',
      label: 'Organization roles',
      render: (row) => {
        const summary = organizationRoleSummary(row)
        return (
          <span className={styles.scope}>
            <StatusBadge status="default">{summary.label}</StatusBadge>
            {summary.detail ? <span>{summary.detail}</span> : null}
            {row.product_overrides?.length ? <span>{row.product_overrides.length} product {row.product_overrides.length === 1 ? 'override' : 'overrides'}</span> : null}
          </span>
        )
      },
    },
    {
      key: 'organizations',
      label: 'Organizations',
      render: (row) => row.organizations.length
        ? row.organizations.map((organization) => organization.name).join(', ')
        : 'None',
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (row) => {
        if (row.invite_expires_at) {
          const expired = new Date(row.invite_expires_at).getTime() <= Date.now()
          return (
            <span className={styles.invitation}>
              <StatusBadge status={expired ? 'error' : 'warning'}>{expired ? 'Invite expired' : 'Pending activation'}</StatusBadge>
              <span className={styles.inviteExpiry}>
                {expired ? 'Since' : 'Expires'} {new Date(row.invite_expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </span>
          )
        }
        if (!row.is_active) return <StatusBadge status="error">Inactive</StatusBadge>
        if (row.pending_activation) return <StatusBadge status="warning">Pending activation</StatusBadge>
        if (!row.organizations.some((organization) => organization.is_active)) {
          return <StatusBadge status="default">No active access</StatusBadge>
        }
        return <StatusBadge status="success">Active</StatusBadge>
      },
    },
    {
      key: 'last_login_at',
      label: 'Last login',
      render: (row) => row.last_login_at ? new Date(row.last_login_at).toLocaleDateString() : 'Never',
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => {
        const isCurrentUser = row.id === currentUser?.id
        const isSuperAdmin = row.organizations.some((organization) => organization.is_active && organization.role === ROLES.SUPER_ADMIN)
        return (
          <div className={styles.actions}>
            <Button
              variant="ghost"
              size="compact"
              onClick={() => openEditor(row)}
              disabled={isCurrentUser || isSuperAdmin || row.pending_activation}
              aria-label={`Edit organization access for ${row.display_name || row.email}`}
            >
              Edit access
            </Button>
            {row.invite_expires_at ? (
              <DropdownMenu triggerLabel="More actions" items={[
                {
                  key: 'resend-invite',
                  label: 'Resend Invite',
                  onClick: () => resendInvitationMutation.mutate(row.id),
                  disabled: reissuingInvite,
                },
                {
                  key: 'copy-invite-link',
                  label: 'Copy Invite Link',
                  onClick: () => createInvitationLink(row.id),
                  disabled: reissuingInvite,
                },
                {
                  key: 'cancel-invite',
                  label: 'Cancel Invite',
                  onClick: () => setCancelInviteUser(row),
                  disabled: cancelInvitationMutation.isPending,
                  danger: true,
                },
              ]} />
            ) : null}
          </div>
        )
      },
    },
  ]

  const loading = usersQuery.isLoading || organizationsQuery.isLoading
  const queryError = usersQuery.error || organizationsQuery.error

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Wager team"
        context="Command Center"
        subtitle="Manage organization scope and roles for operators who work across customers."
        primaryAction={(
          <Button
            onClick={openInvite}
            disabled={organizationsQuery.isLoading || organizationsQuery.isError}
          >
            Add team member
          </Button>
        )}
      />

      <DataView
        toolbar={(
          <PageToolbar
            label="Wager team controls"
            search={<SearchField value={searchTerm} onChange={setSearchTerm} label="Search Wager team" placeholder="Search users or organizations…" />}
            actions={<Badge compact>{filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}</Badge>}
          />
        )}
        loading={loading}
        error={queryError}
        items={filteredUsers}
        filtered={Boolean(searchTerm.trim())}
        emptyTitle="No users available"
        emptyDescription="Add a team member to configure organization access."
        searchEmptyTitle="No users match your search"
        onRetry={() => {
          usersQuery.refetch()
          organizationsQuery.refetch()
        }}
        onClear={() => setSearchTerm('')}
      >
        <DataTable
          columns={columns}
          data={filteredUsers}
          keyField="id"
          density="compact"
          label="Cross-organization user access"
        />
      </DataView>

      {inviteLink ? (
        <InviteLinkDialog url={inviteLink.url} expiresAt={inviteLink.expires_at} onClose={() => setInviteLink(null)} />
      ) : null}

      {cancelInviteUser ? (
        <ConfirmDialog
          open
          title="Cancel invitation"
          message={`Cancel the invitation for ${cancelInviteUser.email}?`}
          confirmLabel="Cancel invitation"
          cancelLabel="Keep invitation"
          isDestructive
          loading={cancelInvitationMutation.isPending}
          onConfirm={() => cancelInvitationMutation.mutate(cancelInviteUser.id)}
          onCancel={() => setCancelInviteUser(null)}
        />
      ) : null}

      {editingUser ? (
        <UserAccessDialog
          user={editingUser}
          organizations={organizations}
          onClose={closeEditor}
          onSubmit={(access) => updateMutation.mutate({ userId: editingUser.id, access })}
          isSubmitting={updateMutation.isPending}
          error={updateMutation.error}
        />
      ) : null}

      {inviting ? (
        <UserAccessDialog
          organizations={organizations}
          onClose={closeInvite}
          onSubmit={(request) => inviteMutation.mutate(request)}
          isSubmitting={inviteMutation.isPending}
          error={inviteMutation.error}
        />
      ) : null}
    </PageLayout>
  )
}

export default WagerTeamPage
